import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space } from '../styles/theme.js'
import { tags as tagsApi } from '../lib/api.js'

// --- Styles ---

const paletteGrid = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${space.xs};
`

const tagBtn = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.1s ease;
  user-select: none;
  &:hover { opacity: 0.85; }
`

const tagBtnActive = css`
  border-color: rgba(0, 0, 0, 0.2) !important;
`

const tagBtnAi = css`
  border-style: dashed !important;
  border-color: rgba(0, 0, 0, 0.15) !important;
`

const shortcutBadge = css`
  font-size: 10px;
  font-family: 'SF Mono', monospace;
  font-weight: ${font.weight.bold};
  opacity: 0.6;
  min-width: 14px;
  text-align: center;
`

const addBtn = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  cursor: pointer;
  border: 2px dashed ${colors.border};
  background: transparent;
  color: ${colors.textMuted};
  transition: all 0.1s ease;
  user-select: none;
  &:hover { border-color: ${colors.brand}; color: ${colors.text}; }
`

const inlineFormStyle = css`
  margin-top: ${space.sm};
  padding: ${space.sm};
  background: ${colors.bg};
  border: 1px solid ${colors.border};
  border-radius: 6px;
`

const inlineFormRow = css`
  display: flex;
  gap: ${space.xs};
  align-items: center;
  margin-bottom: ${space.xs};
`

const inlineInput = css`
  flex: 1;
  padding: 4px 8px;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  outline: none;
  &:focus { border-color: ${colors.brand}; }
`

const colorDot = css`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  flex-shrink: 0;
`

const colorDotSelected = css`
  border-color: ${colors.text};
`

const inlineBtn = css`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  border: none;
  cursor: pointer;
`

const manageLink = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  cursor: pointer;
  margin-top: ${space.sm};
  &:hover { color: ${colors.text}; }
`

const subtagBtn = css`
  margin-left: 12px;
  font-size: ${font.size.xs};
`

const moreBtn = css`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  cursor: pointer;
  border: 1px dashed ${colors.border};
  background: transparent;
  user-select: none;
  &:hover { color: ${colors.text}; border-color: ${colors.textMuted}; }
`

const emptyStyle = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  font-style: italic;
`

const QUICK_COLORS = [
  '#ff5c5c', '#ffa94d', '#ffe66d', '#a9e34b',
  '#4ecdc4', '#74c0fc', '#c792ea', '#999999',
]

// Group tags into parents and children-by-parent.
// Orphaned subtags (parent_id doesn't match any tag) are treated as parents.
export function groupTags(allTags) {
  const tagIds = new Set(allTags.map(t => t.id))
  const parents = []
  const childrenByParent = new Map()

  for (const tag of allTags) {
    if (!tag.parent_id || !tagIds.has(tag.parent_id)) {
      parents.push(tag)
    } else {
      if (!childrenByParent.has(tag.parent_id)) {
        childrenByParent.set(tag.parent_id, [])
      }
      childrenByParent.get(tag.parent_id).push(tag)
    }
  }

  return { parents, childrenByParent }
}

// Build a map: shortcut key → tag id
export function buildShortcutMap(allTags) {
  const map = new Map()
  for (const tag of allTags) {
    if (tag.shortcut) {
      map.set(tag.shortcut.toLowerCase(), tag.id)
    }
  }
  return map
}

// Determine which tag IDs are currently active for a session
export function activeTagIds(session) {
  if (!session?.tags) return new Set()
  return new Set(session.tags.map(t => t.id))
}

// TagPalette component.
// Props:
//   projectId     - current project
//   sessionId     - current session ID
//   sessionTags   - array of tag objects on this session (from getSession)
//   allTags       - full tag list for the project
//   onToggle(tagId, isNowActive, skipAutoAdvance) - callback after a tag is toggled
//   onTagCreated(tag) - callback after a new tag is created (optional)
//   showAll       - if true, force-show all parent tags (controlled by parent via Tab key)
export function TagPalette() {
  let toggling = new Set()
  let showCreateForm = false
  let newTagName = ''
  let newTagColor = QUICK_COLORS[0]
  let creating = false
  let showAllParents = true

  async function toggle(projectId, sessionId, tagId, isActive, onToggle, allTags, sessionTags) {
    if (toggling.has(tagId)) return
    toggling.add(tagId)
    m.redraw()

    try {
      if (isActive) {
        await tagsApi.remove(projectId, sessionId, tagId)
        if (onToggle) onToggle(tagId, false)
      } else {
        // Auto-assign parent if this is a subtag and parent not active
        const tag = allTags.find(t => t.id === tagId)
        if (tag?.parent_id) {
          const parentActive = sessionTags.some(t => t.id === tag.parent_id)
          if (!parentActive) {
            await tagsApi.assign(projectId, sessionId, tag.parent_id)
            if (onToggle) onToggle(tag.parent_id, true, true) // skipAutoAdvance
          }
        }
        await tagsApi.assign(projectId, sessionId, tagId)
        if (onToggle) onToggle(tagId, true)

        // Collapse other parents when a parent with children is activated
        const { childrenByParent } = groupTags(allTags)
        if (childrenByParent.has(tagId)) {
          showAllParents = false
        }
      }
    } catch (err) {
      console.error('Failed to toggle tag:', err)
    } finally {
      toggling.delete(tagId)
      m.redraw()
    }
  }

  async function createAndAssign(projectId, sessionId, onToggle, onTagCreated) {
    if (!newTagName.trim() || creating) return
    creating = true
    m.redraw()

    try {
      const tag = await tagsApi.create(projectId, {
        name: newTagName.trim(),
        color: newTagColor,
      })
      // Assign to current session
      await tagsApi.assign(projectId, sessionId, tag.id)

      // Reset form
      showCreateForm = false
      newTagName = ''
      newTagColor = QUICK_COLORS[0]

      // Notify parent to refresh tags
      if (onTagCreated) onTagCreated(tag)
      if (onToggle) onToggle(tag.id, true)
    } catch (err) {
      console.error('Failed to create tag:', err)
    } finally {
      creating = false
      m.redraw()
    }
  }

  return {
    view(vnode) {
      const { projectId, sessionId, sessionTags, allTags, onToggle, onTagCreated } = vnode.attrs

      // Sync showAll prop from parent (e.g. Tab key toggle)
      if (vnode.attrs.showAll !== undefined) {
        showAllParents = vnode.attrs.showAll
      }

      if (!allTags || allTags.length === 0) {
        return m('div', [
          m('div', { class: emptyStyle }, 'No tags defined.'),
          m('div', {
            class: addBtn,
            style: 'margin-top: 6px;',
            onclick: () => { showCreateForm = true },
          }, '+ New tag'),
          showCreateForm ? renderCreateForm(projectId, sessionId, onToggle, onTagCreated) : null,
          m('div', {
            class: manageLink,
            onclick: () => m.route.set(`/projects/${projectId}/tags`),
          }, 'Manage tags'),
        ])
      }

      const active = activeTagIds({ tags: sessionTags })
      const { parents, childrenByParent } = groupTags(allTags)

      // Determine if any parent with children is active
      const anyParentWithChildrenActive = parents.some(
        p => childrenByParent.has(p.id) && active.has(p.id)
      )

      // Build tag buttons imperatively to avoid keyed/non-keyed Mithril issues
      const tagButtons = []
      let hiddenCount = 0

      for (const tag of parents) {
        const hasChildren = childrenByParent.has(tag.id)
        const isActive = active.has(tag.id)

        // Hide inactive parents with children when another parent is expanded
        if (hasChildren && !isActive && anyParentWithChildrenActive && !showAllParents) {
          hiddenCount++
          continue
        }

        const isToggling = toggling.has(tag.id)
        tagButtons.push(renderTagButton(tag, isActive, isToggling, projectId, sessionId, onToggle, allTags, sessionTags, false))

        // Show children when parent is active
        if (isActive && hasChildren) {
          for (const child of childrenByParent.get(tag.id)) {
            const childActive = active.has(child.id)
            const childToggling = toggling.has(child.id)
            tagButtons.push(renderTagButton(child, childActive, childToggling, projectId, sessionId, onToggle, allTags, sessionTags, true))
          }
        }
      }

      // "+ N more" button when parents are hidden
      if (hiddenCount > 0) {
        tagButtons.push(m('div', {
          key: '__more__',
          class: moreBtn,
          onclick: () => { showAllParents = true },
        }, `+ ${hiddenCount} more`))
      }

      tagButtons.push(m('div', {
        key: '__add__',
        class: addBtn,
        onclick: () => { showCreateForm = !showCreateForm },
      }, '+ New'))

      return m('div', [
        m('div', { class: paletteGrid }, tagButtons),
        showCreateForm ? renderCreateForm(projectId, sessionId, onToggle, onTagCreated) : null,
        m('div', {
          class: manageLink,
          onclick: () => m.route.set(`/projects/${projectId}/tags`),
        }, 'Manage tags'),
      ])
    },
  }

  function renderTagButton(tag, isActive, isToggling, projectId, sessionId, onToggle, allTags, sessionTags, isSubtag) {
    const isAi = isActive && sessionTags.some(t => t.id === tag.id && t.source === 'ai')
    return m('div', {
      key: tag.id,
      class: `${tagBtn} ${isActive ? tagBtnActive : ''} ${isAi ? tagBtnAi : ''} ${isSubtag ? subtagBtn : ''}`,
      style: `
        background: ${isActive ? (tag.color || colors.border) : colors.border};
        opacity: ${isToggling ? '0.5' : '1'};
      `,
      title: isAi ? 'AI-predicted tag' : '',
      onclick: () => toggle(projectId, sessionId, tag.id, isActive, onToggle, allTags, sessionTags),
    }, [
      tag.name,
      tag.shortcut
        ? m('span', { class: shortcutBadge }, tag.shortcut.toUpperCase())
        : null,
    ])
  }

  function renderCreateForm(projectId, sessionId, onToggle, onTagCreated) {
    return m('div', { class: inlineFormStyle }, [
      m('div', { class: inlineFormRow }, [
        m('input', {
          class: inlineInput,
          placeholder: 'Tag name',
          value: newTagName,
          oninput: (e) => { newTagName = e.target.value },
          onkeydown: (e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              e.stopPropagation()
              createAndAssign(projectId, sessionId, onToggle, onTagCreated)
            }
            if (e.key === 'Escape') {
              e.preventDefault()
              e.stopPropagation()
              showCreateForm = false
            }
          },
          oncreate: (vnode) => vnode.dom.focus(),
        }),
      ]),
      m('div', { class: inlineFormRow },
        QUICK_COLORS.map(c =>
          m('div', {
            key: c,
            class: `${colorDot} ${newTagColor === c ? colorDotSelected : ''}`,
            style: `background: ${c};`,
            onclick: () => { newTagColor = c },
          })
        )
      ),
      m('div', { style: `display:flex;gap:${space.xs};justify-content:flex-end;` }, [
        m('button', {
          class: inlineBtn,
          style: `background:${colors.bgCard};color:${colors.textSecondary};border:1px solid ${colors.border};`,
          onclick: () => { showCreateForm = false },
        }, 'Cancel'),
        m('button', {
          class: inlineBtn,
          style: `background:${colors.brand};color:white;`,
          disabled: !newTagName.trim() || creating,
          onclick: () => createAndAssign(projectId, sessionId, onToggle, onTagCreated),
        }, creating ? 'Creating...' : 'Create'),
      ]),
    ])
  }
}
